import { ChecklightSessionStatus } from '@thesavingsgroup/enums/ChecklightSessionStatus';
import { useEffect } from 'react';

import { useLatestRef } from '../useLatestRef/useLatestRef';
import { UsePollWhileQueuedForSubmissionParams } from './types/UsePollWhileQueuedForSubmissionParams';

const POLL_INTERVAL = 20_000;

/**
 * Polls for a change to the QUEUED_FOR_SUBMISSION application status,
 * using stable function refs to prevent polling from restarting on rerenders.
 * (ex. opening/closing modals, interacting with task cards/banners, etc.)
 */
export const usePollWhileQueuedForSubmission = ({
  shouldPoll,
  fetchUserVehicles,
  getCurrentVehicleStatus,
  onStatusChange,
}: UsePollWhileQueuedForSubmissionParams): void => {
  const fetchUserVehiclesRef = useLatestRef(fetchUserVehicles);
  const getCurrentVehicleStatusRef = useLatestRef(getCurrentVehicleStatus);
  const onStatusChangeRef = useLatestRef(onStatusChange);

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    let stopped = false;

    const tick = async () => {
      if (stopped) {
        return;
      }

      // We fetch all user vehicles for multiple reasons:
      // 1. The application page being viewed must also be updated with the latest current vehicle data.
      // 2. The endpoint to fetch all user vehicles picks up on any changes to vehicle applications and updates their statuses accordingly.
      const vehicles = await fetchUserVehiclesRef.current();

      if (stopped) {
        return;
      }

      // By this point, the current user context has been updated with the latest vehicle data
      // this function will find the current vehicle in the context and return its status.
      const status = getCurrentVehicleStatusRef.current();

      // Here we are determining if the status has changed from QUEUED_FOR_SUBMISSION to something else.
      // If it has, we call the onStatusChange callback with the latest vehicles data.
      if (status && status !== ChecklightSessionStatus.QUEUED_FOR_SUBMISSION) {
        onStatusChangeRef.current(vehicles);
      }
    };

    const interval = setInterval(tick, POLL_INTERVAL);

    return (): void => {
      stopped = true;
      clearInterval(interval);
    };
  }, [shouldPoll]);
};
